import ErrorIcon from '@mui/icons-material/Error'
import { Alert, Card, Grid, Stack, Typography } from '@mui/material'
import type { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { EDisbursementTarget } from '@src/types/constants'
import { useMemo, useState } from 'react'
import type { Control, FieldValues } from 'react-hook-form'
import { useController, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddButton from '../Common/Button/AddButton'
import type { DisbursementLineSchemaType, DisbursementSchemaType } from './loan-form-schema'
import DisbursementDialog from './DisbursementDialog'
import DisbursementItem from './DisbursementItem'

interface Props<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, DisbursementSchemaType>
}

const DisbursementList = <TFieldValues extends FieldValues = FieldValues>({ control, name }: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const { append, remove, update, fields } = useFieldArray({
    control,
    name,
    keyName: 'keyId',
  })
  const values = fields as unknown as DisbursementSchemaType[]

  const {
    fieldState: { error },
  } = useController({ name: name as never, control })
  const [open, setOpen] = useState(false)
  const [targetType, setTargetType] = useState<EDisbursementTarget>(EDisbursementTarget.Borrower)

  const renderList = useMemo(
    () => (
      <Stack gap={1}>
        {values.map((disbursement, index) => (
          <DisbursementItem
            key={index}
            disbursement={disbursement}
            control={control}
            name={`${name}.${index}.lines` as FieldArrayPathByValue<TFieldValues, DisbursementLineSchemaType>}
            onDelete={() => remove(index)}
            onUpdate={(d) => update(index, d as never)}
          />
        ))}
      </Stack>
    ),
    [values, control, name, remove, update],
  )

  return (
    <Grid marginTop={1} size={12}>
      <Card variant="outlined" sx={{ padding: 1 }}>
        <DisbursementDialog
          open={open}
          payeeType={targetType}
          onClose={() => setOpen(false)}
          onSubmit={(data) => append(data as never)}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h6">{t('loan.disbursements')}</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <AddButton
              onClick={() => {
                setTargetType(EDisbursementTarget.Borrower)
                setOpen(true)
              }}
            >
              {t('loan.addBorrower')}
            </AddButton>
            <AddButton
              onClick={() => {
                setTargetType(EDisbursementTarget.Merchant)
                setOpen(true)
              }}
            >
              {t('loan.addMerchant')}
            </AddButton>
          </Stack>
        </Stack>
        {error?.message && (
          <Alert icon={<ErrorIcon fontSize="inherit" />} severity="error">
            {error?.message}
          </Alert>
        )}
        {renderList}
      </Card>
    </Grid>
  )
}

export default DisbursementList
